import { Button } from "@/components/ui/button";
import { Icons } from "@/lib/icons";
import { useSubscription } from "@/hooks/useSubscription";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { track } from "@/lib/analytics";

const BENEFITS = [
  { icon: Icons.chat, title: "Talk to Claira", text: "Compassionate, private conversations whenever you need them." },
  { icon: Icons.journal, title: "Journaling", text: "Voice or text entries with gentle AI reflections." },
  { icon: Icons.resources, title: "Curated resources", text: "Trusted, evidence-based help tailored to you." },
  { icon: Icons.insights, title: "Personalised insights", text: "See patterns across your screenings and check-ins." },
];

interface Props {
  variant?: "page" | "inline";
  title?: string;
}

export function SubscribeGate({ variant = "page", title }: Props) {
  const { user } = useAuth();
  const { isTrialing, daysLeft } = useSubscription();
  const [busy] = useState(false);
  const [plan, setPlan] = useState<"claira_plus_monthly" | "claira_plus_yearly">("claira_plus_monthly");
  const [showCheckout, setShowCheckout] = useState(false);

  function subscribe() {
    if (!user) {
      window.location.href = "/auth";
      return;
    }
    track("subscribe_clicked", { plan });
    setShowCheckout(true);
  }

  if (showCheckout) {
    return (
      <div className={variant === "page" ? "mx-auto max-w-2xl space-y-4" : "space-y-4"}>
        <PaymentTestModeBanner />
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl font-semibold">Start your free trial</h2>
          <Button variant="ghost" size="sm" onClick={() => setShowCheckout(false)}>Back</Button>
        </div>
        <p className="text-sm text-muted-foreground">7 days free, then {plan === "claira_plus_monthly" ? "£4.99/month" : "£39.99/year"}. Cancel anytime before the trial ends — you won't be charged.</p>
        <StripeEmbeddedCheckout priceId={plan} />
      </div>
    );
  }

  return (
    <div
      className={
        variant === "page"
          ? "mx-auto max-w-3xl space-y-8"
          : "rounded-3xl border border-border/60 bg-card p-6 shadow-card space-y-6"
      }
    >
      <PaymentTestModeBanner />
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
          <Icons.premium className="h-3.5 w-3.5" /> Claira Plus
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
          {title ?? "Unlock the full Claira experience"}
        </h1>
        <p className="text-muted-foreground max-w-xl mx-auto">
          Screenings stay free, forever. A subscription gives you continuous, compassionate support
          between check-ins — so you're never alone with what you're carrying.
        </p>
        {isTrialing && daysLeft != null && (
          <p className="text-sm text-warm-foreground">You're on a free trial — {daysLeft} day{daysLeft === 1 ? "" : "s"} left.</p>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {BENEFITS.map((b) => (
          <div key={b.title} className="rounded-2xl border border-border/60 bg-card p-4 flex gap-3">
            <b.icon className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">{b.title}</div>
              <div className="text-sm text-muted-foreground">{b.text}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-3xl border border-border/60 bg-gradient-brand/5 p-6 text-center space-y-4">
        <div className="inline-flex rounded-full border border-border/60 bg-card p-1 text-sm">
          <button
            onClick={() => setPlan("claira_plus_monthly")}
            className={`px-4 py-1.5 rounded-full transition-colors ${plan === "claira_plus_monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
          >Monthly</button>
          <button
            onClick={() => setPlan("claira_plus_yearly")}
            className={`px-4 py-1.5 rounded-full transition-colors ${plan === "claira_plus_yearly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
          >Yearly · save 33%</button>
        </div>
        <div className="space-y-1">
          {plan === "claira_plus_monthly" ? (
            <div className="font-display text-3xl font-semibold">£4.99<span className="text-base text-muted-foreground font-sans"> / month</span></div>
          ) : (
            <div className="font-display text-3xl font-semibold">£39.99<span className="text-base text-muted-foreground font-sans"> / year</span></div>
          )}
          <div className="text-sm text-muted-foreground">Early-bird pricing. Cancel anytime.</div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={subscribe} disabled={busy} className="bg-gradient-brand">
            Start 7-day free trial
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">No charge today. We'll remind you before your trial ends.</p>
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Icons.shield className="h-3.5 w-3.5" /> Private, encrypted, GDPR-protected.
        </div>
      </div>

      <ul className="text-sm text-muted-foreground space-y-1.5 max-w-md mx-auto">
        {[
          "Unlimited conversations with Claira",
          "Unlimited journal entries (voice + text)",
          "Personalised resources & coping tools",
          "Trends, patterns, and gentle nudges",
        ].map((t) => (
          <li key={t} className="flex items-center gap-2"><Icons.check className="h-3.5 w-3.5 text-primary" /> {t}</li>
        ))}
      </ul>
    </div>
  );
}

export default SubscribeGate;